import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ActionButton, PageLayout } from '../../components/DesignSystem';
import API_URL from '../../config/api';
import { Card, Input, Select, Space, Button, message, Modal, Table, Spin } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { ListPagination, LIST_PAGE_SIZE } from '../../components/ListPagination';
import '../../styles/listPage.css';

const CATEGORY_LIST_CACHE_KEY = 'shaho_category_list_v1';
const CATEGORY_LIST_CACHE_MS = 5 * 60 * 1000;

interface Category {
  id: string;
  name: string;
  type?: string;
  deleted_at?: string;
  image?: string;
  description?: string;
  status?: string;
  notes?: string;
  productCount?: number;
  stockValue?: number;
}

function loadCategoryListCache(): Category[] | null {
  try {
    const raw = sessionStorage.getItem(CATEGORY_LIST_CACHE_KEY);
    if (!raw) return null;
    const { at, rows } = JSON.parse(raw);
    if (Date.now() - at > CATEGORY_LIST_CACHE_MS || !Array.isArray(rows)) return null;
    return rows;
  } catch {
    return null;
  }
}

function saveCategoryListCache(rows: Category[]) {
  try {
    sessionStorage.setItem(CATEGORY_LIST_CACHE_KEY, JSON.stringify({ at: Date.now(), rows }));
  } catch { /* quota */ }
}

function mapCategoryRow(c: any): Category {
  return {
    id:           c._id  || c.id,
    name:         c.name,
    description:  c.description  || '',
    image:        c.image         || '',
    status:       c.status        || 'Active',
    notes:        c.notes         || '',
    deleted_at:   c.deleted_at,
    productCount: Number(c.product_count ?? c.productCount ?? 0),
    stockValue:   Number(c.stock_value ?? c.stockValue ?? 0),
  };
}

const CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const initialCache = loadCategoryListCache();
  const [categories, setCategories] = useState<Category[]>(initialCache ?? []);
  const [loading, setLoading] = useState(!initialCache?.length);
  const [listPage, setListPage] = useState(1);
  const [filter, setFilter] = useState<string>('active');
  const [search, setSearch] = useState('');
  const csvImportRef = useRef<HTMLInputElement>(null);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvImportRows, setCsvImportRows] = useState<any[]>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportErrors, setCsvImportErrors] = useState<Array<{ row: number; error: string }>>([]);

  const fetchCategories = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? categories.length > 0;
    try {
      if (!silent) setLoading(true);
      const headers: Record<string,string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const url = `${API_URL}/categories?include_archived=true`;
      const res = await apiFetch(url, { headers });
      const text = await res.text();
      let data: any;
      try { data = JSON.parse(text); } catch (e) { data = text; }

      if (!res.ok) {
        const msg = (data && (data.message || data.error)) || `HTTP ${res.status}`;
        if (!silent) {
          message.error(msg);
          setCategories([]);
        }
        return;
      }

      const array = Array.isArray(data) ? data : data.categories || [];
      const mapped = array.map(mapCategoryRow);
      setCategories(mapped);
      saveCategoryListCache(mapped);
    } catch (err) {
      console.error('Failed to load categories', err);
      if (!silent) {
        message.error('Failed to load categories');
        setCategories([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      // require login for categories (backend enforces auth)
      navigate('/login');
      return;
    }

    fetchCategories({ silent: !!initialCache?.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setListPage(1);
  }, [search, filter]);

  const filteredCategories = categories
    .filter(c => {
      if (filter === 'archived') return !!c.deleted_at;
      if (filter === 'all') return true;
      if (filter === 'active') return !c.deleted_at && c.status !== 'Inactive';
      if (filter === 'inactive') return !c.deleted_at && c.status === 'Inactive';
      return true;
    })
    .filter(c => {
      if (!search.trim()) return true;
      const lower = search.toLowerCase();
      return c.name.toLowerCase().includes(lower) || (c.description || '').toLowerCase().includes(lower);
    });

  const categoryPages = Math.max(1, Math.ceil(filteredCategories.length / LIST_PAGE_SIZE));
  const safeListPage = Math.min(listPage, categoryPages);
  const paginatedCategories = filteredCategories.slice(
    (safeListPage - 1) * LIST_PAGE_SIZE,
    safeListPage * LIST_PAGE_SIZE
  );

  const exportCategoriesCSV = () => {
    if (!filteredCategories.length) {
      message.warning('No categories to export');
      return;
    }

    const escape = (value: any) => {
      const text = value == null ? '' : String(value);
      if (/['",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const headers = ['ID', 'Name', 'Description', 'Type', 'Product Count', 'Stock Value', 'Status', 'Archived'];
    const rows = filteredCategories.map(c => [
      c.id,
      c.name,
      c.description,
      c.type || '',
      c.productCount ?? 0,
      c.stockValue ?? 0,
      c.status || 'Active',
      c.deleted_at ? 'Yes' : 'No',
    ].map(escape).join(','));

    const csv = `data:text/csv;charset=utf-8,${headers.map(escape).join(',')}\n${rows.join('\n')}`;
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', `categories_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = (ev.target?.result as string) || '';
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { message.warning('CSV must have a header row + at least one data row'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const obj: Record<string, string> = {};
        headers.forEach((h, idx) => { obj[h] = cols[idx] || ''; });
        return obj;
      });
      setCsvImportRows(rows);
      setCsvImportErrors([]);
      setShowCsvImportModal(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const submitCsvCategoryImport = async () => {
    setCsvImporting(true);
    setCsvImportErrors([]);
    try {
      const res = await apiFetch(`${API_URL}/categories/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categories: csvImportRows.map(r => ({
            name: r.name,
            description: r.description || '',
            status: r.status || 'Active',
          })),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setShowCsvImportModal(false);
        setCsvImportRows([]);
        message.success(`Imported ${data.imported} categor${data.imported !== 1 ? 'ies' : 'y'} successfully`);
        fetchCategories();
      } else {
        setCsvImportErrors(data?.errors || [{ row: 0, error: data?.error || 'Import failed' }]);
      }
    } catch {
      setCsvImportErrors([{ row: 0, error: 'Network error' }]);
    } finally {
      setCsvImporting(false);
    }
  };

  const archiveCategory = async (id: string, name: string) => {
    const reason = window.prompt(`⚠️ Archive category "${name}"?\n\nThis will hide the category from lists.\n\nPlease enter a reason for archiving:`);
    if (!reason) return;

    try {
      if (!token) { showToast('Please login to archive categories'); navigate('/login'); return; }

      const res = await apiFetch(`${API_URL}/categories/${id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) {
        setCategories(prev => prev.filter(c => c.id !== id));
      } else {
        const data = await res.json();
        message.error(data.message || 'Failed to archive category');
      }
    } catch (e) {
      console.error('Archive error', e);
      message.error('Network error while archiving');
    }
  };

  const restoreCategory = async (id: string, name: string) => {
    if (!window.confirm(`Restore category "${name}"?`)) return;

    try {
      if (!token) { showToast('Please login to restore categories'); navigate('/login'); return; }

      const res = await apiFetch(`${API_URL}/categories/${id}/restore`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCategories();
      } else {
        const data = await res.json();
        message.error(data.message || 'Failed to restore category');
      }
    } catch (e) {
      console.error('Restore error', e);
      message.error('Network error while restoring');
    }
  };

  const renderCategoryActions = (c: Category) => (
    <div style={s.actions}>
      {c.deleted_at ? (
        <ActionButton variant="edit" label="Restore" onClick={() => restoreCategory(c.id, c.name)} />
      ) : (
        <>
          <ActionButton variant="view" label="View" onClick={() => navigate(`/categories/view/${c.id}`)} />
          <ActionButton variant="edit" label="Edit" onClick={() => navigate(`/categories/edit/${c.id}`)} />
          <ActionButton variant="delete" label="Archive" onClick={() => archiveCategory(c.id, c.name)} />
        </>
      )}
    </div>
  );

  return (
    <PageLayout
      title="🗂️ Categories"
      action={<ActionButton variant="add" label="Add Category" onClick={() => navigate('/categories/add')} />}
    >
      {/* STATS BANNER */}
      <Card style={{ marginBottom: 24 }}>
        <Space size="large">
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Total Categories</div>
            <div style={{ fontSize: 22 }}>{categories.length}</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Total Products</div>
            <div style={{ fontSize: 22 }}>{categories.reduce((sum, c) => sum + (c.productCount || 0), 0)}</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Total Stock Value</div>
            <div style={{ fontSize: 22 }}>£{categories.reduce((sum, c) => sum + (c.stockValue || 0), 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
          </div>
        </Space>
      </Card>
      <Card>
        {/* FILTER BAR */}
        <div style={s.filterBar}>
          <Space wrap>
            <Input
              placeholder="Search categories by name or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
            />
            <Select value={filter} onChange={setFilter} style={{ width: 180 }}>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="all">All</Select.Option>
              <Select.Option value="archived">Archived</Select.Option>
            </Select>
            <Space>
              <input ref={csvImportRef} type="file" accept=".csv" onChange={handleCsvImportFile} style={{ display: 'none' }} />
              <Button onClick={() => csvImportRef.current?.click()}>📥 Import CSV</Button>
              <Button icon={<DownloadOutlined />} onClick={exportCategoriesCSV}>Export CSV</Button>
            </Space>
          </Space>
        </div>

        <ListPagination
          total={categories.length}
          filtered={filteredCategories.length}
          page={safeListPage}
          pages={categoryPages}
          onPageChange={setListPage}
          noun="categories"
        />

        {/* TABLE — desktop */}
        <div className="list-table-panel">
        <div style={s.tableWrapper}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /> Loading categories…</div>
          ) : paginatedCategories.length > 0 ? (
            <table style={s.table}>
              <thead>
                <tr style={s.headerRow}>
                  <th style={s.th}>Image</th>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Description</th>
                  <th style={s.th}>Product Count</th>
                  <th style={s.th}>Stock Value</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.map(c => (
                    <tr key={c.id} style={s.row}>
                      <td style={s.td}>
                        {c.image ? <img src={c.image} alt="Category" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} /> : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      <td style={s.td}>
                        {c.name}
                        {c.deleted_at && <span style={s.archivedBadge}>ARCHIVED</span>}
                      </td>
                      <td style={s.td}>
                        {c.description ? <span title={c.description}>{c.description.slice(0, 40)}{c.description.length > 40 ? '…' : ''}</span> : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      <td style={s.td}>{c.productCount || 0}</td>
                      <td style={s.td}>£{Number(c.stockValue || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                      <td style={s.td}>{c.status || 'Active'}</td>
                      <td style={s.td}>
                        {renderCategoryActions(c)}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>📭</div>
              <h3>No categories found</h3>
              <p>{search ? 'Try a different search term' : 'Add your first category to get started'}</p>
              <button onClick={() => navigate('/categories/add')} style={s.addBtn}>➕ Add Category</button>
            </div>
          )}
        </div>
        </div>

        {/* Mobile cards */}
        <div className="list-mobile-cards">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /> Loading categories…</div>
          ) : paginatedCategories.length > 0 ? paginatedCategories.map(c => (
            <div key={c.id} className="list-mobile-card">
              <div className="list-mobile-card-header">
                {c.image ? (
                  <img src={c.image} alt={c.name} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🗂️</div>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="list-mobile-card-title">
                    {c.name}
                    {c.deleted_at && <span style={s.archivedBadge}>ARCHIVED</span>}
                  </div>
                  <div className="list-mobile-card-meta">{c.description ? c.description.slice(0, 60) : 'No description'}</div>
                </div>
              </div>
              <div className="list-mobile-card-info">
                <div><div className="list-mobile-card-label">Products</div>{c.productCount || 0}</div>
                <div><div className="list-mobile-card-label">Stock value</div>£{Number(c.stockValue || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                <div><div className="list-mobile-card-label">Status</div>{c.status || 'Active'}</div>
              </div>
              <div className="list-mobile-actions">{renderCategoryActions(c)}</div>
            </div>
          )) : (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>📭</div>
              <h3>No categories found</h3>
              <p>{search ? 'Try a different search term' : 'Add your first category to get started'}</p>
              <button onClick={() => navigate('/categories/add')} style={s.addBtn}>➕ Add Category</button>
            </div>
          )}
        </div>
      </Card>
      <Modal
        title="Import Categories from CSV"
        open={showCsvImportModal}
        onOk={submitCsvCategoryImport}
        onCancel={() => setShowCsvImportModal(false)}
        okText={csvImporting ? 'Importing…' : 'Import'}
        confirmLoading={csvImporting}
      >
        <p style={{ fontSize: 13, color: '#6b7280' }}>CSV columns: name, description, status</p>
        {csvImportErrors.length > 0 && (
          <div style={{ marginBottom: 12, padding: 12, background: '#fef2f2', borderRadius: 8 }}>
            {csvImportErrors.map((e, i) => (
              <div key={i} style={{ fontSize: 13, color: '#dc2626' }}>Row {e.row}: {e.error}</div>
            ))}
          </div>
        )}
        <Table
          size="small"
          pagination={false}
          dataSource={csvImportRows.slice(0, 50).map((r, i) => ({ ...r, key: i }))}
          columns={[
            { title: 'Name', dataIndex: 'name' },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Status', dataIndex: 'status' },
          ]}
        />
      </Modal>
    </PageLayout>
  );
};

const s: any = {
  filterBar: {marginBottom: 20, display: 'flex', gap: 15, alignItems: 'center', justifyContent: 'space-between'},
  archivedBadge: {display: 'inline-block', padding: '4px 10px', background: '#ff4444', color: '#fff', borderRadius: 12, fontSize: 11, fontWeight: 700, marginLeft: 10},
  tableWrapper: {overflowX: 'auto', WebkitOverflowScrolling: 'touch'},
  table: {width: '100%', minWidth: 400, borderCollapse: 'collapse'},
  headerRow: {background: '#fafafa'},
  th: {padding: 16, textAlign: 'left', fontSize: 13, fontWeight: 500, color: 'rgba(0,0,0,0.85)', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap'},
  row: {borderBottom: '1px solid #e2e8f0', transition: 'background 0.2s'},
  td: {padding: '16px 20px', fontSize: 14},
  actions: {display: 'flex', gap: 8, flexWrap: 'wrap'},
  addBtn: {padding: '12px 24px', background: '#2ECC40', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15, boxShadow: '0 2px 8px rgba(46,204,64,0.3)'},
  emptyState: {textAlign: 'center', padding: 80},
  emptyIcon: {fontSize: 64, marginBottom: 20}
};

export default CategoryList;
